import { computed, inject } from '@angular/core';
import { updateState, withDevtools, withDevToolsStub, withGlitchTracking } from '@angular-architects/ngrx-toolkit';
import { signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { ConfigService } from '../services/config/config.service';
import { ActivityLogService } from '../services/activity-tracking/activity-log.service';
import { FarmStore } from './farm.store';
import { ActivityLogItem } from '../entities/activity-tracking/activity-log-item.entities';
import {
    ActivityLogItemQueryParams,
    ActivityLogItemFilters,
    ActivityLogItemCreateRequest,
    ActivityLogItemUpdateRequest
} from '../entities/activity-tracking/activity-log-item-api.types';
import { EntityCreatedResponse } from '../models/api/api-response.model';
import { withCondition } from './utils/with-condition';
import { upsertMany, sortByDateField } from '../helpers/data.helpers';

interface ActivityLogState {
    activityLogs: ActivityLogItem[];
    selectedActivityLog: ActivityLogItem | null;
    loading: boolean;
    error: string | null;
    totalActivityLogs: number;
    currentPage: number;
    pageSize: number;
    hasMoreActivityLogs: boolean;
    searchQuery: string;
    filters: ActivityLogItemFilters;
}

const initialState: ActivityLogState = {
    activityLogs: [],
    selectedActivityLog: null,
    loading: false,
    error: null,
    totalActivityLogs: 0,
    currentPage: 0,
    pageSize: 100,
    hasMoreActivityLogs: false,
    searchQuery: '',
    filters: {},
};

export const ActivityLogStore = signalStore(
    withState(initialState),
    withProps(() => ({
        configService: inject(ConfigService),
        activityLogService: inject(ActivityLogService),
        farmStore: inject(FarmStore),
    })),
    withComputed(({ activityLogs, selectedActivityLog, loading, error, hasMoreActivityLogs }) => ({
        hasActivityLogs: computed(() => activityLogs().length > 0),
        activityLogsCount: computed(() => activityLogs().length),
        isLoading: computed(() => loading()),
        hasError: computed(() => error() !== null),
        selectedActivityLogAction: computed(() => selectedActivityLog()?.action || null),
        hasMoreActivityLogsAvailable: computed(() => hasMoreActivityLogs()),
    })),
    withCondition(({ configService }) => !configService?.environment?.production,
        withDevtools('activity-log', withGlitchTracking()),
        withDevToolsStub('activity-log'),
    ),
    withMethods((store) => ({
        loadActivityLogs(params?: ActivityLogItemQueryParams, filters?: ActivityLogItemFilters, append = false): void {
            updateState(store, '[ActivityLog] Load Activity Logs Start', { loading: true, error: null });

            const currentFarmId = store.farmStore.currentFarm()?.id;
            const farmFilters = {
                ...filters,
                ...(currentFarmId && { farm_id: currentFarmId })
            };

            const sortedParams: ActivityLogItemQueryParams = {
                ...params,
                sort: params?.sort || 'created_at',
                order: params?.order || 'desc'
            };

            store.activityLogService.getActivityLogs(sortedParams, farmFilters).subscribe({
                next: (response) => {
                    const mergedLogs = append ? upsertMany(store.activityLogs(), response.data) : response.data;
                    const newActivityLogs = sortByDateField(mergedLogs, 'created_at', 'desc');

                    updateState(store, '[ActivityLog] Load Activity Logs Success', {
                        activityLogs: newActivityLogs,
                        totalActivityLogs: response.total,
                        currentPage: params?.skip ? Math.floor((params.skip + response.data.length) / (params?.limit || 100)) : 0,
                        pageSize: params?.limit || 100,
                        hasMoreActivityLogs: (params?.skip || 0) + response.data.length < response.total,
                        loading: false,
                        error: null,
                        filters: filters || {},
                        searchQuery: ''
                    });
                },
                error: (error) => {
                    updateState(store, '[ActivityLog] Load Activity Logs Error', {
                        loading: false,
                        error: error.message || 'Failed to load activity logs'
                    });
                }
            });
        },

        searchActivityLogs(query: string, params?: Omit<ActivityLogItemQueryParams, 'q'>): void {
            updateState(store, '[ActivityLog] Search Activity Logs Start', { loading: true, error: null, searchQuery: query });

            const currentFarmId = store.farmStore.currentFarm()?.id;
            const searchParams: ActivityLogItemQueryParams = {
                ...params,
                q: query,
                sort: params?.sort || 'created_at',
                order: params?.order || 'desc'
            };
            const farmFilters = {
                ...(currentFarmId && { farm_id: currentFarmId })
            };

            store.activityLogService.getActivityLogs(searchParams, farmFilters).subscribe({
                next: (response) => {
                    const mergedLogs = upsertMany(store.activityLogs(), response.data);
                    const sortedActivityLogs = sortByDateField(mergedLogs, 'created_at', 'desc');
                    updateState(store, '[ActivityLog] Search Activity Logs Success', {
                        activityLogs: sortedActivityLogs,
                        loading: false,
                        error: null,
                        searchQuery: query
                    });
                },
                error: (error) => {
                    updateState(store, '[ActivityLog] Search Activity Logs Error', {
                        loading: false,
                        error: error.message || 'Failed to search activity logs'
                    });
                }
            });
        },

        loadActivityLogById(id: string): void {
            updateState(store, '[ActivityLog] Load Activity Log By ID Start', { loading: true, error: null });
            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Activity Log] Load Activity Log By ID Error', {
                    loading: false,
                    error: 'Farm ID is required to get activity log'
                });
                return;
            }

            store.activityLogService.getActivityLogById(id, farmId).subscribe({
                next: (activityLog) => {
                    const existingIndex = store.activityLogs().findIndex(al => al.id === id);
                    const newActivityLogs = [...store.activityLogs()];
                    if (existingIndex >= 0) {
                        newActivityLogs[existingIndex] = activityLog;
                    } else {
                        newActivityLogs.push(activityLog);
                    }
                    updateState(store, '[ActivityLog] Load Activity Log By ID Success', {
                        activityLogs: newActivityLogs,
                        loading: false,
                        error: null
                    });
                },
                error: (error) => {
                    updateState(store, '[ActivityLog] Load Activity Log By ID Error', {
                        loading: false,
                        error: error.message || 'Failed to load activity log'
                    });
                }
            });
        },

        createActivityLog(activityLogData: ActivityLogItemCreateRequest): void {
            updateState(store, '[ActivityLog] Create Activity Log Start', { loading: true, error: null });
            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Activity Log] Create Activity Log Error', {
                    loading: false,
                    error: 'Farm ID is required to create activity log'
                });
                return;
            }

            store.activityLogService.createActivityLog(activityLogData, farmId).subscribe({
                next: (response: EntityCreatedResponse) => {
                    const newActivityLog: ActivityLogItem = {
                        ...activityLogData,
                        id: response.id,
                        farm_id: farmId,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    };

                    updateState(store, '[ActivityLog] Create Activity Log Success', {
                        activityLogs: [...store.activityLogs(), newActivityLog],
                        totalActivityLogs: store.totalActivityLogs() + 1,
                        loading: false,
                        error: null
                    });
                },
                error: (error) => {
                    updateState(store, '[ActivityLog] Create Activity Log Error', {
                        loading: false,
                        error: error.message || 'Failed to create activity log'
                    });
                }
            });
        },

        updateActivityLog(id: string, updates: ActivityLogItemUpdateRequest): void {
            updateState(store, '[ActivityLog] Update Activity Log Start', { loading: true, error: null });
            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Activity Log] Update Activity Log Error', {
                    loading: false,
                    error: 'Farm ID is required to update activity log'
                });
                return;
            }

            store.activityLogService.updateActivityLog(id, updates, farmId).subscribe({
                next: () => {
                    const currentActivityLog = store.activityLogs().find(al => al.id === id);
                    if (!currentActivityLog) {
                        updateState(store, '[ActivityLog] Update Activity Log Error', {
                            loading: false,
                            error: 'Activity log not found in store after update'
                        });
                        return;
                    }

                    const updatedActivityLog: ActivityLogItem = {
                        ...currentActivityLog,
                        ...updates,
                        updated_at: new Date().toISOString()
                    };

                    const newActivityLogs = store.activityLogs().map(activityLog =>
                        activityLog.id === id ? updatedActivityLog : activityLog
                    );
                    updateState(store, '[ActivityLog] Update Activity Log Success', {
                        activityLogs: newActivityLogs,
                        loading: false,
                        error: null
                    });
                    if (store.selectedActivityLog()?.id === id) {
                        updateState(store, '[ActivityLog] Update Selected Activity Log', { selectedActivityLog: updatedActivityLog });
                    }
                },
                error: (error) => {
                    updateState(store, '[ActivityLog] Update Activity Log Error', {
                        loading: false,
                        error: error.message || 'Failed to update activity log'
                    });
                }
            });
        },

        deleteActivityLog(id: string): void {
            updateState(store, '[ActivityLog] Delete Activity Log Start', { loading: true, error: null });
            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Activity Log] Delete Activity Log Error', {
                    loading: false,
                    error: 'Farm ID is required to delete activity log'
                });
                return;
            }

            store.activityLogService.deleteActivityLog(id, farmId).subscribe({
                next: () => {
                    const newActivityLogs = store.activityLogs().filter(activityLog => activityLog.id !== id);
                    updateState(store, '[ActivityLog] Delete Activity Log Success', {
                        activityLogs: newActivityLogs,
                        totalActivityLogs: Math.max(0, store.totalActivityLogs() - 1),
                        loading: false,
                        error: null
                    });
                    if (store.selectedActivityLog()?.id === id) {
                        updateState(store, '[ActivityLog] Clear Selected Activity Log', { selectedActivityLog: null });
                    }
                },
                error: (error) => {
                    updateState(store, '[ActivityLog] Delete Activity Log Error', {
                        loading: false,
                        error: error.message || 'Failed to delete activity log'
                    });
                }
            });
        },

        setLoading(loading: boolean): void {
            updateState(store, '[ActivityLog] Set Loading', { loading });
        },

        selectActivityLog(activityLog: ActivityLogItem | null): void {
            updateState(store, '[ActivityLog] Select Activity Log', { selectedActivityLog: activityLog });
        },

        selectActivityLogById(activityLogId: string): void {
            const activityLog = store.activityLogs().find(al => al.id === activityLogId) || null;
            this.selectActivityLog(activityLog);
        },

        setFilters(filters: ActivityLogItemFilters): void {
            updateState(store, '[ActivityLog] Set Filters', { filters });
            this.loadActivityLogs(undefined, filters);
        },

        clearFilters(): void {
            updateState(store, '[ActivityLog] Clear Filters', { filters: {}, searchQuery: '' });
            this.loadActivityLogs();
        },

        clearError(): void {
            updateState(store, '[ActivityLog] Clear Error', { error: null });
        },

        reset(): void {
            updateState(store, '[ActivityLog] Reset', initialState);
        },

        getActivityLogById(id: string): ActivityLogItem | undefined {
            return store.activityLogs().find(activityLog => activityLog.id === id);
        },

        hasAccessToActivityLog(activityLogId: string): boolean {
            return store.activityLogs().some(activityLog => activityLog.id === activityLogId);
        }
    }))
);
